/**
 * Selectable-row style tokens — shared by floating menus, command palettes,
 * and layer lists.
 *
 * Every highlightable row in PoseRef ends with the same inline style pair:
 *
 *   background: isActive ? COLOR_ACTIVE_BG        : "transparent"
 *   color:      isActive ? COLOR_ACCENT | COLOR_TEXT : <dim variant>
 *
 * Two variants exist in the wild:
 *   - "accent"  — bright active background, accent-colored text (menu items,
 *                 command palette, layer list)
 *   - "subtle"  — softer background, plain text color (hierarchy, folder
 *                 rows; not yet using this helper)
 *
 * Call `getSelectableRowStyle(active, "accent")` and spread the result
 * onto your row, or pluck just `.background` if your row already owns its
 * text color independently.
 */

import {
  COLOR_ACCENT,
  COLOR_ACTIVE_BG,
  COLOR_ACTIVE_BG_SUBTLE,
  COLOR_TEXT,
  COLOR_TEXT_DIM,
  COLOR_TEXT_SECONDARY,
} from "./tokens.ts";

export type SelectableRowVariant = "accent" | "accent-dim" | "subtle";

export interface SelectableRowStyle {
  background: string;
  color: string;
}

/**
 * Return the `{ background, color }` pair for a row in one of its
 * selectable states. Consumers may spread the whole object onto their
 * style, or pluck a single key.
 *
 * Variants:
 * - `"accent"`    → active uses `COLOR_ACTIVE_BG` + `COLOR_ACCENT` text,
 *                   inactive uses `COLOR_TEXT_SECONDARY`. Used by floating
 *                   menus and the command palette.
 * - `"accent-dim"` → same active state, inactive uses `COLOR_TEXT_DIM`.
 *                   Used by the layer list and panel list.
 * - `"subtle"`    → softer `COLOR_ACTIVE_BG_SUBTLE` background, active
 *                   uses `COLOR_TEXT`, inactive uses `COLOR_TEXT_SECONDARY`.
 *                   Used by hierarchy rows.
 */
export function getSelectableRowStyle(
  active: boolean,
  variant: SelectableRowVariant = "accent",
): SelectableRowStyle {
  if (variant === "subtle") {
    return {
      background: active ? COLOR_ACTIVE_BG_SUBTLE : "transparent",
      color: active ? COLOR_TEXT : COLOR_TEXT_SECONDARY,
    };
  }
  if (variant === "accent-dim") {
    return {
      background: active ? COLOR_ACTIVE_BG : "transparent",
      color: active ? COLOR_ACCENT : COLOR_TEXT_DIM,
    };
  }
  return {
    background: active ? COLOR_ACTIVE_BG : "transparent",
    color: active ? COLOR_ACCENT : COLOR_TEXT_SECONDARY,
  };
}
